import { ChevronDownIcon, ChevronUpIcon, InfoIcon, SaveIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import Box from '../../../uikit/Box';
import { useMobxInstance } from '../../../utils/mobxUtil';
import { apiClient } from '../../../utils/apiClient';

/**
 * 공통 입력 필드 컴포넌트
 */
interface StrategyInfoInputsProps {
    strategyName: string;
    description: string;
    onStrategyNameChange: (name: string) => void;
    onDescriptionChange: (desc: string) => void;
}

const StrategyInfoInputs = observer(
    ({
        strategyName,
        description,
        onStrategyNameChange,
        onDescriptionChange,
    }: StrategyInfoInputsProps) => {
        return (
            <div className="flex flex-col gap-4">
                {/* 전략명 */}
                <div>
                    <label className="text-base-content/70 mb-2 block text-sm font-semibold">
                        이름
                    </label>
                    <input
                        type="text"
                        placeholder={'이 전략을 뭐라고 부를까요?'}
                        value={strategyName}
                        className="input input-bordered w-full"
                        onChange={(e) => onStrategyNameChange(e.target.value)}
                    />
                </div>

                {/* 전략 설명 */}
                <div>
                    <label className="text-base-content/70 mb-2 block text-sm font-semibold">
                        설명
                    </label>
                    <textarea
                        placeholder={'어떤 생각으로 이 종목들을 선택하셨나요?'}
                        value={description}
                        className="textarea textarea-bordered w-full"
                        rows={3}
                        onChange={(e) => onDescriptionChange(e.target.value)}
                    />
                </div>
            </div>
        );
    },
);

/**
 * 수정 페이지용 전략 정보 박스
 * - 접기/펼치기 가능
 * - 별도 저장 버튼 없음 (페이지 하단 버튼 사용)
 */
interface StrategyInfoEditBoxProps {
    initialExpanded: boolean;
    strategyName: string;
    description: string;
    onStrategyNameChange: (name: string) => void;
    onDescriptionChange: (desc: string) => void;
}

export const StrategyInfoEditBox = observer(
    ({
        initialExpanded,
        strategyName,
        description,
        onStrategyNameChange,
        onDescriptionChange,
    }: StrategyInfoEditBoxProps) => {
        const [isExpanded, setIsExpanded] = useState(initialExpanded);

        return (
            <Box>
                <button
                    className="flex w-full items-center justify-between"
                    onClick={(e) => {
                        e.preventDefault();
                        setIsExpanded(!isExpanded);
                    }}
                >
                    <h3 className="text-base-content/70 flex min-h-8.5 items-center text-sm font-semibold">
                        전략 정보 (선택)
                    </h3>
                    {isExpanded ? (
                        <ChevronUpIcon className="size-5" />
                    ) : (
                        <ChevronDownIcon className="size-5" />
                    )}
                </button>

                {isExpanded && (
                    <div className="mt-4">
                        <StrategyInfoInputs
                            strategyName={strategyName}
                            description={description}
                            onStrategyNameChange={onStrategyNameChange}
                            onDescriptionChange={onDescriptionChange}
                        />
                    </div>
                )}
            </Box>
        );
    },
);

/**
 * 상세 페이지용 전략 정보 가이드 박스
 * - 이름/설명이 비어있을 때만 표시
 * - 인라인 편집 + 저장 버튼
 * - 저장 시 바로 API 호출
 */
class GuideBoxViewStore {
    strategyHash = '';
    strategyName = '';
    description = '';
    isSubmitting = false;

    constructor(strategyHash: string, initialName: string, initialDescription: string) {
        makeAutoObservable(this);
        this.strategyHash = strategyHash;
        this.strategyName = initialName;
        this.description = initialDescription;
    }

    setStrategyName(name: string) {
        this.strategyName = name;
    }

    setDescription(desc: string) {
        this.description = desc;
    }

    get canSubmit() {
        return this.strategyName.trim().length > 0 || this.description.trim().length > 0;
    }

    async submit() {
        if (!this.canSubmit || this.isSubmitting) {
            return false;
        }

        this.isSubmitting = true;

        try {
            await apiClient.strategy.member.update.mutate({
                hash: this.strategyHash,
                name: this.strategyName,
                description: this.description,
            });

            // 캐시 갱신
            apiClient.strategy.public.getStrategy.$invalidate({ hash: this.strategyHash });

            return true;
        } catch (error) {
            console.error('전략 정보 수정 실패:', error);
            return false;
        } finally {
            runInAction(() => {
                this.isSubmitting = false;
            });
        }
    }
}

interface StrategyInfoGuideBoxProps {
    strategyHash: string;
    strategyName: string;
    description: string;
    onSaved?: () => void;
}

export const StrategyInfoGuideBox = observer(
    ({ strategyHash, strategyName, description, onSaved }: StrategyInfoGuideBoxProps) => {
        const viewStore = useMobxInstance(
            GuideBoxViewStore,
            strategyHash,
            strategyName,
            description,
        );

        const handleSubmit = async () => {
            const success = await viewStore.submit();
            if (success && onSaved) {
                onSaved();
            }
        };

        return (
            <Box>
                <Box.Title title="전략 정보 업데이트" />

                <div className="alert alert-soft alert-info mb-4 text-sm">
                    <InfoIcon className="size-4" />
                    전략 이름을 다듬고 설명을 덧붙여보세요.
                </div>

                <StrategyInfoInputs
                    strategyName={viewStore.strategyName}
                    description={viewStore.description}
                    onStrategyNameChange={(name) => viewStore.setStrategyName(name)}
                    onDescriptionChange={(desc) => viewStore.setDescription(desc)}
                />

                <button
                    className="btn btn-soft btn-primary btn-block mt-4"
                    disabled={!viewStore.canSubmit || viewStore.isSubmitting}
                    onClick={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    {viewStore.isSubmitting ? (
                        <>
                            <span className="loading loading-spinner loading-sm"></span>
                            저장 중...
                        </>
                    ) : (
                        <>업데이트 하기</>
                    )}
                </button>
            </Box>
        );
    },
);
